import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "@/lib/api";
import {
  getRememberedLocale,
  localizeApiError,
  rememberLocale,
} from "@/lib/authMessages";
import { getSessionTimeoutCopy } from "@/lib/sessionTimeoutI18n";

/*
 * Global authentication context.
 *
 * Three-state model:
 *   status === "loading"        — still checking the server for a session
 *   status === "authenticated"  — server confirmed a valid session; `user` populated
 *   status === "unauthenticated"— no session; user must log in
 *
 * The current user object matches the backend's `_public_user()` shape.
 */

const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const IDLE_WARNING_MS = 55 * 60 * 1000;
const ACTIVITY_PING_THROTTLE_MS = 30 * 1000;

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const lastActivityPingRef = useRef(0);
  const pendingTakeoverEmailRef = useRef(null);

  const clearIdleTimers = useCallback(() => {
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const scheduleIdleTimers = useCallback(() => {
    if (typeof window === "undefined") return;
    clearIdleTimers();
    warningTimerRef.current = window.setTimeout(() => {
      setIdleWarningOpen(true);
    }, IDLE_WARNING_MS);
    logoutTimerRef.current = window.setTimeout(() => {
      void api.post("/auth/logout").catch(() => {
        // Server-side idle expiry and MongoDB TTL remain the fallback cleanup.
      });
      setIdleWarningOpen(false);
      setUser(null);
      setStatus("unauthenticated");
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimers]);

  const applyUser = useCallback((u) => {
    if (u) {
      setUser(u);
      setStatus("authenticated");
      if (u.preferred_language) rememberLocale(u.preferred_language);
    } else {
      setUser(null);
      setStatus("unauthenticated");
      setIdleWarningOpen(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data.user);
      return data.user;
    } catch {
      applyUser(null);
      return null;
    }
  }, [applyUser]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("loading");
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const takeover = pendingTakeoverEmailRef.current === normalizedEmail;
    try {
      const { data } = await api.post(
        "/auth/login",
        { email, password },
        takeover ? { headers: { "X-MEC-Session-Takeover": "true" } } : undefined,
      );
      pendingTakeoverEmailRef.current = null;
      applyUser(data.user);
      return data.user;
    } catch (err) {
      if (err?.response?.data?.detail?.code === "active_login_exists") {
        pendingTakeoverEmailRef.current = normalizedEmail;
      } else {
        pendingTakeoverEmailRef.current = null;
      }
      throw err;
    }
  }, [applyUser]);

  const register = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    applyUser(data.user);
    return data.user;
  }, [applyUser]);

  const logout = useCallback(async () => {
    clearIdleTimers();
    setIdleWarningOpen(false);
    pendingTakeoverEmailRef.current = null;
    try {
      await api.post("/auth/logout");
    } catch {
      /* swallow */
    }
    applyUser(null);
  }, [applyUser, clearIdleTimers]);

  const sendMeaningfulActivity = useCallback(async (force = false) => {
    if (status !== "authenticated") return;

    const now = Date.now();
    scheduleIdleTimers();
    setIdleWarningOpen(false);

    if (!force && now - lastActivityPingRef.current < ACTIVITY_PING_THROTTLE_MS) {
      return;
    }
    lastActivityPingRef.current = now;

    try {
      await api.post("/auth/activity");
    } catch (err) {
      if (err?.response?.status === 401) {
        clearIdleTimers();
        applyUser(null);
      }
    }
  }, [applyUser, clearIdleTimers, scheduleIdleTimers, status]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (status !== "authenticated") {
      clearIdleTimers();
      return undefined;
    }

    lastActivityPingRef.current = Date.now();
    scheduleIdleTimers();

    const activityHandler = () => {
      void sendMeaningfulActivity(false);
    };

    window.addEventListener("pointerdown", activityHandler, { passive: true });
    window.addEventListener("keydown", activityHandler);
    window.addEventListener("touchstart", activityHandler, { passive: true });

    return () => {
      window.removeEventListener("pointerdown", activityHandler);
      window.removeEventListener("keydown", activityHandler);
      window.removeEventListener("touchstart", activityHandler);
      clearIdleTimers();
    };
  }, [clearIdleTimers, scheduleIdleTimers, sendMeaningfulActivity, status]);

  const updateProfile = useCallback(async (firstName, country) => {
    const { data } = await api.post("/auth/profile", {
      first_name: firstName,
      country,
    });
    applyUser(data.user);
    return data.user;
  }, [applyUser]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      status,
      user,
      refresh,
      login,
      register,
      logout,
      updateProfile,
      changePassword,
    }),
    [status, user, refresh, login, register, logout, updateProfile, changePassword],
  );

  const timeoutCopy = getSessionTimeoutCopy();

  return (
    <AuthContext.Provider value={value}>
      {children}
      {idleWarningOpen && status === "authenticated" && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-timeout-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 id="session-timeout-title" className="text-xl font-semibold text-slate-900">
              {timeoutCopy.title}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              {timeoutCopy.body}
            </p>
            <button
              type="button"
              className="mt-5 w-full rounded-lg bg-slate-900 px-4 py-2.5 font-medium text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
              onClick={() => void sendMeaningfulActivity(true)}
            >
              {timeoutCopy.continue}
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function formatApiErrorDetail(err, locale) {
  return localizeApiError(err, locale || getRememberedLocale());
}
